import React from "react";
import { Table } from "antd";
import { ColumnsType } from "antd/es/table";
import { formatLkr } from "../../utils/currency";

function fmtLkr(v: any) {
  return formatLkr(Number(v || 0));
}

function sum(data: any[], key: string): number {
  return data.reduce((acc, row) => acc + Number(row[key] || 0), 0);
}

interface SummaryProps {
  data: any[];
  columns: ColumnsType<any>;
}

function GenericSummary({ data, columns, keysToSum }: SummaryProps & { keysToSum: string[] }) {
  if (data.length === 0) return null;
  return (
    <Table.Summary fixed>
      <Table.Summary.Row style={{ fontWeight: 600, background: "#fafafa" }}>
        {columns.map((col: any, i: number) => {
          const key = col.dataIndex || col.key;
          const isSum = keysToSum.includes(key);
          const val = i === 0 && !isSum ? "TOTAL" : isSum ? sum(data, key) : "";
          const isMoney = typeof col.render === "function";
          return (
            <Table.Summary.Cell key={key} index={i} align={col.align || "left"}>
              {typeof val === "number" && val !== 0
                ? isMoney
                  ? fmtLkr(val)
                  : val.toLocaleString()
                : typeof val === "number"
                  ? isMoney
                    ? fmtLkr(0)
                    : "0"
                  : val}
            </Table.Summary.Cell>
          );
        })}
      </Table.Summary.Row>
    </Table.Summary>
  );
}

// ---- 1. Daily Sales ----
export function dailySalesSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={[
      "restaurant_revenue", "room_revenue", "laundry_revenue", "service_revenue",
    ]} />
  );
}

// ---- 2. Restaurant Sales ----
export function restaurantSalesSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={[
      "order_count", "subtotal", "discounts", "service_charges", "taxes", "total_revenue",
    ]} />
  );
}

// ---- 3. Take Away Sales ----
export function takeAwaySalesSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={[
      "order_count", "subtotal", "discounts", "taxes", "total_revenue",
    ]} />
  );
}

// ---- 4. Dine-In Sales ----
export function dineInSalesSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={[
      "order_count", "tables_used", "total_revenue",
    ]} />
  );
}

// ---- 5. Room Service Sales ----
export function roomServiceSalesSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={[
      "order_count", "rooms_served", "total_revenue",
    ]} />
  );
}

// ---- 6. Room Revenue ----
export function roomRevenueSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={[
      "rooms_occupied", "room_revenue",
    ]} />
  );
}

// ---- 7. Meal Breakdown ----
export function mealBreakdownSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={[
      "breakfast", "lunch", "dinner", "total",
    ]} />
  );
}

// ---- 8. Monthly Revenue ----
export function monthlyRevenueSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={[
      "bookings", "revenue",
    ]} />
  );
}

// ---- 9. Bookings by Room Type ----
export function bookingsByRoomTypeSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={[
      "total_bookings", "total_revenue",
    ]} />
  );
}

// ---- 10. Laundry ----
export function laundrySummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={[
      "order_count", "total_revenue",
    ]} />
  );
}

// ---- 11. Ironing ----
export function ironingSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={[
      "order_count", "total_revenue",
    ]} />
  );
}

// ---- 12. Other Services ----
export function otherServicesSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={[
      "quantity", "total",
    ]} />
  );
}

// ---- 13. Car Rental ----
export function carRentalSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={[
      "quantity", "total",
    ]} />
  );
}

// ---- 14. Motorbike Rental ----
export function motorbikeRentalSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={[
      "quantity", "total",
    ]} />
  );
}

// ---- 15. Car Wash ----
export function carWashSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={[
      "quantity", "total",
    ]} />
  );
}

// ---- 16. Cashier ----
export function cashierSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={[
      "transaction_count", "total_collected",
    ]} />
  );
}

// ---- 17. Payments ----
export function paymentsSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={[
      "transaction_count", "total_amount", "completed", "pending_count", "failed",
    ]} />
  );
}

// ---- 18. Outstanding Balances ----
export function outstandingBalancesSummary(data: any[], columns: ColumnsType<any>) {
  if (data.length === 0) return null;
  const totalCharges = sum(data, "total_charges");
  const totalPayments = sum(data, "total_payments");
  const balance = totalCharges - totalPayments;
  return (
    <Table.Summary fixed>
      <Table.Summary.Row style={{ fontWeight: 600, background: "#fafafa" }}>
        <Table.Summary.Cell index={0}>TOTAL</Table.Summary.Cell>
        <Table.Summary.Cell index={1} />
        <Table.Summary.Cell index={2} />
        <Table.Summary.Cell index={3} />
        <Table.Summary.Cell index={4} />
        <Table.Summary.Cell index={5} align="right">{fmtLkr(totalCharges)}</Table.Summary.Cell>
        <Table.Summary.Cell index={6} align="right">{fmtLkr(totalPayments)}</Table.Summary.Cell>
        <Table.Summary.Cell index={7} align="right">{fmtLkr(balance)}</Table.Summary.Cell>
      </Table.Summary.Row>
    </Table.Summary>
  );
}

// ---- 19. Guest Folio ----
export function guestFolioSummary(data: any[], columns: ColumnsType<any>) {
  if (data.length === 0) return null;
  return (
    <Table.Summary fixed>
      <Table.Summary.Row style={{ fontWeight: 600, background: "#fafafa" }}>
        <Table.Summary.Cell index={0}>TOTAL</Table.Summary.Cell>
        <Table.Summary.Cell index={1} />
        <Table.Summary.Cell index={2} />
        <Table.Summary.Cell index={3} align="right">{sum(data, "quantity")}</Table.Summary.Cell>
        <Table.Summary.Cell index={4} />
        <Table.Summary.Cell index={5} align="right">{fmtLkr(sum(data, "discount"))}</Table.Summary.Cell>
        <Table.Summary.Cell index={6} align="right">{fmtLkr(sum(data, "tax"))}</Table.Summary.Cell>
        <Table.Summary.Cell index={7} align="right">{fmtLkr(sum(data, "total"))}</Table.Summary.Cell>
        <Table.Summary.Cell index={8} />
      </Table.Summary.Row>
    </Table.Summary>
  );
}

// ---- 20. Weekly Statement ----
export function weeklyStatementSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={["amount"]} />
  );
}

// ---- 21. Monthly Statement ----
export function monthlyStatementSummary(data: any[], columns: ColumnsType<any>) {
  if (data.length === 0) return null;
  return (
    <Table.Summary fixed>
      <Table.Summary.Row style={{ fontWeight: 600, background: "#fafafa" }}>
        <Table.Summary.Cell index={0}>TOTAL</Table.Summary.Cell>
        <Table.Summary.Cell index={1} align="right">{fmtLkr(sum(data, "room_revenue"))}</Table.Summary.Cell>
        <Table.Summary.Cell index={2} align="right">{fmtLkr(sum(data, "restaurant_revenue"))}</Table.Summary.Cell>
        <Table.Summary.Cell index={3} align="right">{fmtLkr(sum(data, "laundry_revenue"))}</Table.Summary.Cell>
        <Table.Summary.Cell index={4} align="right">{fmtLkr(sum(data, "service_revenue"))}</Table.Summary.Cell>
        <Table.Summary.Cell index={5} align="right">{fmtLkr(sum(data, "total_revenue"))}</Table.Summary.Cell>
        <Table.Summary.Cell index={6} align="right">{fmtLkr(sum(data, "total_payments"))}</Table.Summary.Cell>
        <Table.Summary.Cell index={7} align="right">{fmtLkr(sum(data, "total_revenue") - sum(data, "total_payments"))}</Table.Summary.Cell>
      </Table.Summary.Row>
    </Table.Summary>
  );
}

// ---- 22. Tax / VAT ----
export function taxSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={["tax_amount"]} />
  );
}

// ---- 23. Discounts ----
export function discountsSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={["discount_amount"]} />
  );
}

// ---- 24. Void / Cancelled ----
export function voidCancelledSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={["amount"]} />
  );
}

// ---- 25. Item Sales ----
export function itemSalesSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={[
      "times_ordered", "total_quantity", "total_revenue",
    ]} />
  );
}

// ---- 26. Category Sales ----
export function categorySalesSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={[
      "order_count", "items_sold", "total_revenue",
    ]} />
  );
}

// ---- 27. Top Guests ----
export function topGuestsSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={[
      "total_stays", "total_spent",
    ]} />
  );
}

// ---- 28. Finance (general ledger) ----
export function financeProfitLossSummary(data: any[], _columns: ColumnsType<any>) {
  const total = (section: string) =>
    data.filter((r) => r.section === section).reduce((s, r) => s + Number(r.amount || 0), 0);
  const income = total("Income");
  const cogs = total("Cost of sales");
  const opex = total("Operating expense");
  const net = income - cogs - opex;

  return (
    <Table.Summary fixed>
      <Table.Summary.Row style={{ fontWeight: 600, background: "#fafafa" }}>
        <Table.Summary.Cell index={0}>Net profit</Table.Summary.Cell>
        <Table.Summary.Cell index={1} />
        <Table.Summary.Cell index={2}>
          {fmtLkr(income)} income − {fmtLkr(cogs)} cost − {fmtLkr(opex)} opex
        </Table.Summary.Cell>
        <Table.Summary.Cell index={3} align="right">
          {fmtLkr(net)}
        </Table.Summary.Cell>
      </Table.Summary.Row>
    </Table.Summary>
  );
}

export function financeTrialBalanceSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={["opening", "debit", "credit", "closing"]} />
  );
}

export function financeDayBookSummary(data: any[], columns: ColumnsType<any>) {
  return <GenericSummary data={data} columns={columns} keysToSum={["debit", "credit"]} />;
}

export function financeCashBookSummary(data: any[], columns: ColumnsType<any>) {
  return <GenericSummary data={data} columns={columns} keysToSum={["money_in", "money_out", "net"]} />;
}

export function financeDailyCashSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary
      data={data}
      columns={columns}
      keysToSum={["receipts", "payments", "deposits_to_bank"]}
    />
  );
}

export function financeDailyCardSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary
      data={data}
      columns={columns}
      keysToSum={["collections", "settled_gross", "fees", "settled_net"]}
    />
  );
}

export function financeDailyBankSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary
      data={data}
      columns={columns}
      keysToSum={["cash_deposits", "card_settlements", "other_in", "money_out"]}
    />
  );
}

export function financeReconciliationSummary(data: any[], columns: ColumnsType<any>) {
  return <GenericSummary data={data} columns={columns} keysToSum={["count", "amount"]} />;
}

// KPI rows carry no nights / revenue, so the totals are the per-room split —
// which is the point: the sum must agree with the control on the report.
export function financeRoomPerformanceSummary(data: any[], columns: ColumnsType<any>) {
  return <GenericSummary data={data} columns={columns} keysToSum={["nights_sold", "room_revenue"]} />;
}

// KPI rows carry no orders / revenue either, so the totals are the per-channel
// split — which is what the source-vs-ledger control is measured against.
export function financeRestaurantPerformanceSummary(data: any[], columns: ColumnsType<any>) {
  return <GenericSummary data={data} columns={columns} keysToSum={["orders", "revenue"]} />;
}

// The value column is the whole report; nothing else adds up.
export function financeStockValuationSummary(data: any[], columns: ColumnsType<any>) {
  return <GenericSummary data={data} columns={columns} keysToSum={["value"]} />;
}

// Net / tax / total all add up across the register; status and supplier do not.
export function financePurchaseRegisterSummary(data: any[], columns: ColumnsType<any>) {
  return <GenericSummary data={data} columns={columns} keysToSum={["amount", "tax", "total"]} />;
}

// Every money column is a sum of its lines; the counts are what is left.
export function financePayrollRegisterSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary
      data={data}
      columns={columns}
      keysToSum={[
        "basic_salary",
        "allowances",
        "gross_amount",
        "epf_etf",
        "apit",
        "net_pay",
        "employer_cost",
      ]}
    />
  );
}

// Only `amount` adds up — days overdue and bucket position are descriptions
// of a debt, not quantities of it.
export function financeApAgingSummary(data: any[], columns: ColumnsType<any>) {
  return <GenericSummary data={data} columns={columns} keysToSum={["amount"]} />;
}

export function financeArAgingSummary(data: any[], columns: ColumnsType<any>) {
  return <GenericSummary data={data} columns={columns} keysToSum={["amount"]} />;
}

// The year's total, summed over the months that have a figure. `GenericSummary`
// reads through `Number(v || 0)`, so a month outside the ledger contributes
// nothing — which is the point: those months are not zero months, they are
// not months, and folding them in would turn a partial year into a full one.
export function financeAnnualPnlSummary(data: any[], columns: ColumnsType<any>) {
  return (
    <GenericSummary data={data} columns={columns} keysToSum={["income", "expense", "profit"]} />
  );
}

// ---- Lookup map ----
export const REPORT_SUMMARIES: Record<string, (data: any[], columns: ColumnsType<any>) => React.ReactNode> = {
  "daily-sales": dailySalesSummary,
  "restaurant-sales": restaurantSalesSummary,
  "take-away-sales": takeAwaySalesSummary,
  "dine-in-sales": dineInSalesSummary,
  "room-service-sales": roomServiceSalesSummary,
  "room-revenue": roomRevenueSummary,
  "meal-breakdown": mealBreakdownSummary,
  "monthly-revenue": monthlyRevenueSummary,
  "bookings-by-room-type": bookingsByRoomTypeSummary,
  "laundry": laundrySummary,
  "ironing": ironingSummary,
  "other-services": otherServicesSummary,
  "car-rental": carRentalSummary,
  "motorbike-rental": motorbikeRentalSummary,
  "car-wash": carWashSummary,
  "cashier": cashierSummary,
  "payments": paymentsSummary,
  "outstanding-balances": outstandingBalancesSummary,
  "guest-folio": guestFolioSummary,
  "weekly-statement": weeklyStatementSummary,
  "monthly-statement": monthlyStatementSummary,
  "tax": taxSummary,
  "discounts": discountsSummary,
  "void-cancelled": voidCancelledSummary,
  "item-sales": itemSalesSummary,
  "category-sales": categorySalesSummary,
  "top-guests": topGuestsSummary,
  "finance-profit-loss": financeProfitLossSummary,
  "finance-trial-balance": financeTrialBalanceSummary,
  "finance-day-book": financeDayBookSummary,
  "finance-cash-book": financeCashBookSummary,
  "finance-daily-cash": financeDailyCashSummary,
  "finance-daily-card": financeDailyCardSummary,
  "finance-daily-bank": financeDailyBankSummary,
  "finance-reconciliation": financeReconciliationSummary,
  "finance-room-performance": financeRoomPerformanceSummary,
  "finance-restaurant-performance": financeRestaurantPerformanceSummary,
  "finance-stock-valuation": financeStockValuationSummary,
  "finance-purchase-register": financePurchaseRegisterSummary,
  "finance-payroll-register": financePayrollRegisterSummary,
  "finance-ap-aging": financeApAgingSummary,
  "finance-ar-aging": financeArAgingSummary,
  "finance-annual-pnl": financeAnnualPnlSummary,
};
